import { SubmitMissionProgressType } from '@aboutyou/legacy-shared/src/types/missions'
import { TrackingPageTypeWithoutId } from '@aboutyou/legacy-tracking-sdk'
import TopBrands from 'components/TopBrands'
import { topNavBrands } from 'constants/categoryIds'
import { useScrollIntoView } from 'helpers/hooks/useScrollIntoView'
import { useTranslation } from 'helpers/hooks/useTranslation'
import { PageTrackingContextProvider } from 'helpers/tracking/PageTrackingContext'
import { useTrackingPageUrl } from 'helpers/tracking/hooks/useTrackingPageUrl'
import React, { FC, useRef } from 'react'
import { useBrandOverviewPageStore, useUserStore } from 'store/context'
import styled from 'styled-components'
import { mediaDesktopAndUp } from 'uiComponents/constants/customMedia'
import { fsM } from 'uiComponents/constants/typography'
import StickyBrandSearch from './components/BrandSearch'
import BrandsList from './components/BrandsList'
import DocumentHead from './components/DocumentHead'
import HeroSlider from './components/HeroSlider'
import { BrandsOverviewRecommendations } from './components/Recommendations/Recommendations'
import { useTopBrandRecommendations } from './useTopBrandRecommendations'

interface BrandOverviewPageProps {
  shouldScrollToSearchRegion: boolean
  children?: React.ReactNode
  updateSearchContext?: (term: string) => void
  submitLikeBrandMission?: SubmitMissionProgressType
}

const BrandsOverviewRecommendationsWrapper = styled.div`
  position: relative;
  margin-top: 30px;

  @media ${mediaDesktopAndUp} {
    margin-top: 60px;
  }
`

const TopBrandsWrapper = styled.div`
  .topBrandsWrapper {
    margin-bottom: 30px;

    @media ${mediaDesktopAndUp} {
      margin-bottom: 60px;
    }
  }
`

const BrandSearchHeadline = styled.h1`
  font-size: ${fsM}px;
  letter-spacing: normal;
  line-height: normal;
  margin-bottom: 5px;
  padding-bottom: 10px;
`

const BrandsPage: FC<BrandOverviewPageProps> = ({
  shouldScrollToSearchRegion,
  updateSearchContext,
  submitLikeBrandMission,
}) => {
  const brandSearchContainerRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()
  const gender = useUserStore(store => store.gender)
  const brandsList = useBrandOverviewPageStore(store => store.brandsList)

  const { topBrandRecommendationIds } = useTopBrandRecommendations(topNavBrands)

  const searchHeadlineRef = useRef<HTMLHeadingElement>(null)

  useScrollIntoView(shouldScrollToSearchRegion, searchHeadlineRef, 500)

  return (
    <>
      <DocumentHead />
      <div className={'container-fluid container-fluid-1920'}>
        <HeroSlider />
      </div>

      <div className="container">
        {gender !== 'kids' && (
          <BrandsOverviewRecommendationsWrapper>
            <BrandsOverviewRecommendations />
          </BrandsOverviewRecommendationsWrapper>
        )}
        <TopBrandsWrapper>
          <TopBrands brandIds={topBrandRecommendationIds.slice(0, 10)} />
        </TopBrandsWrapper>
        {brandsList.length > 0 && (
          <>
            <BrandSearchHeadline
              data-testid="BrandSearchHeadline"
              id={t('markensuche_url')}
              ref={searchHeadlineRef}
            >
              {t('Entdecke alle Marken auf ABOUT YOU')}
            </BrandSearchHeadline>
            <div ref={brandSearchContainerRef}>
              <StickyBrandSearch
                container={brandSearchContainerRef}
                updateSearchContext={updateSearchContext}
              />
              <BrandsList submitLikeBrandMission={submitLikeBrandMission} />
            </div>
          </>
        )}
      </div>
    </>
  )
}

const TrackedBrandsPage: FC<
  Partial<
    Pick<
      BrandOverviewPageProps,
      'updateSearchContext' | 'submitLikeBrandMission'
    >
  >
> = ({ updateSearchContext, submitLikeBrandMission }) => {
  const { path, search, hash } = useTrackingPageUrl()
  const { t } = useTranslation()
  const shouldScrollToSearchRegion =
    hash.replace('#', '') === t('markensuche_url')

  return (
    <PageTrackingContextProvider
      path={path}
      search={search}
      type={TrackingPageTypeWithoutId.BrandOverview}
    >
      <BrandsPage
        shouldScrollToSearchRegion={shouldScrollToSearchRegion}
        updateSearchContext={updateSearchContext}
        submitLikeBrandMission={submitLikeBrandMission}
      />
    </PageTrackingContextProvider>
  )
}

export default TrackedBrandsPage
